<template>
  <v-container fluid class="pa-0 ma-0">
    <v-row>
      <v-col>
        <v-card outlined class="px-4 ma-2">
          <v-card-title class="pa-0 my-4">Supported languages</v-card-title>
          <p>Check languages you want your project to support</p>
          <div
            ref="langList"
            class="inline-list"
            :class="{ 'inline-list--collapsed': !showAllLangs }"
          >
            <v-checkbox
              v-for="lang in langList"
              :key="lang.id"
              v-model="projectSettings.selectedLangs"
              :label="lang.name"
              :value="lang.id"
              :disabled="
                projectSettings.selectedLangs.length == 1 &&
                  lang.id == projectSettings.selectedLangs[0]
              "
              color="primary"
              class="mr-4 flex-grow-0"
              @change="selectDefaultLang(), updateChangeDetected(true)"
            ></v-checkbox>
          </div>
          <v-btn
            v-if="langsOverflow || showAllLangs"
            text
            x-small
            class="mb-3 grey--text"
            @click="showAllLangs = !showAllLangs"
          >
            <v-icon x-small left>
              {{ showAllLangs ? "mdi-chevron-up" : "mdi-chevron-down" }}
            </v-icon>
            {{ showAllLangs ? "Show less" : "Show all" }}
          </v-btn>

          <v-select
            :items="langOptions"
            item-text="name"
            item-value="id"
            label="Default language"
            outlined
            @change="updateChangeDetected(true)"
            v-model="projectSettings.defaultLangId"
            :menu-props="{ top: false, offsetY: true }"
            style="max-width: 300px"
            class="mx-0 mb-3"
            hide-details="auto"
          ></v-select>
        </v-card>
        <v-card outlined class="px-4 ma-2">
          <v-card-title class="pa-0 my-4"
            >Project colors and stylesheet</v-card-title
          >

          <!-- Live stylesheet preview -->
          <div
            class="stylesheet-preview mb-4"
            :style="{ background: parsedVars['--v-offBlack'] || '#1a1a1a' }"
          >
            <!-- Floating pill app bar (mirrors MainAppBar: ma-6, rounded pill, primary-opaque bg, box-shadow) -->
            <div class="preview-appbar-wrapper">
              <div
                class="preview-appbar"
                :style="{ background: parsedVars['--v-primary-opaque'] || '#00578caa' }"
              >
                <div class="preview-appbar-left">
                  <v-icon x-small :color="parsedVars['--v-secondary-base'] || '#abb8c3'">
                    mdi-arrow-left-circle-outline
                  </v-icon>
                  <img
                    v-if="projectSettings.logo"
                    :src="projectSettings.logo"
                    class="preview-logo ml-2"
                    :style="logoScaleStyle"
                    alt="logo"
                  />
                </div>
                <div class="preview-appbar-center">
                  <span
                    class="preview-appbar-title"
                    :style="{ color: parsedVars['--v-secondary-base'] || '#abb8c3' }"
                  >PROJECT NAME</span>
                </div>
                <div class="preview-appbar-right">
                  <v-icon x-small :color="parsedVars['--v-secondary-base'] || '#abb8c3'">mdi-menu</v-icon>
                </div>
              </div>
            </div>

            <!-- Dummy content area -->
            <div class="preview-body">
              <!-- View switch (mirrors ViewSwitch.vue: round blurred buttons with a circle arrow) -->
              <div class="preview-viewswitch">
                <div
                  v-for="dir in ['left', 'right']"
                  :key="dir"
                  class="preview-viewswitch-btn"
                  :style="{ background: viewSwitchBackground }"
                >
                  <v-icon small :color="viewSwitchArrow">
                    {{
                      dir === "left"
                        ? "mdi-arrow-left-circle-outline"
                        : "mdi-arrow-right-circle-outline"
                    }}
                  </v-icon>
                </div>
              </div>

              <!-- Unit card panel -->
              <div
                class="preview-unit-card"
                :style="{ background: parsedVars['--v-highlightedPrimary-base'] || '#00314fd9' }"
              >
                <span
                  class="preview-unit-label"
                  :style="{ color: parsedVars['--v-offWhite'] || '#fefbf5' }"
                >Unit A-12</span>
                <div
                  class="preview-accent-dot"
                  :style="{ background: parsedVars['--v-accent-base'] || '#00cbea' }"
                ></div>
              </div>

              <!-- Action button (mirrors ComponentMainButton: pill, depressed, letter-spacing 2.7px) -->
              <div
                class="preview-action-btn"
                :style="{
                  background: parsedVars['--v-button-background'] || '#00578c',
                  color: parsedVars['--v-button-text'] || '#abb8c3',
                }"
              >View Details</div>

              <!-- Secondary badge -->
              <div
                class="preview-badge"
                :style="{
                  background: parsedVars['--v-primary-opaque'] || '#00578caa',
                  color: parsedVars['--v-offWhite'] || '#fefbf5',
                }"
              >Available</div>
            </div>
          </div>

          <p class="mb-2">Click any swatch to change the color.</p>

          <v-row dense>
            <v-col
              v-for="field in importantVarFields"
              :key="field.key"
              cols="6"
              sm="4"
              md="3"
              class="pb-2"
            >
              <div class="caption grey--text text--lighten-1 mb-1">
                {{ field.label }}
              </div>
              <v-menu
                v-model="field.pickerOpen"
                :close-on-content-click="false"
                offset-y
                min-width="300"
              >
                <template v-slot:activator="{ on }">
                  <div class="color-swatch-btn" v-on="on">
                    <div
                      class="color-swatch-preview"
                      :style="{
                        background: parsedVars[field.key] || 'transparent',
                      }"
                    ></div>
                    <span class="swatch-hex ml-2">
                      {{ parsedVars[field.key] || "—" }}
                    </span>
                  </div>
                </template>
                <v-card>
                  <v-color-picker
                    :value="parsedVars[field.key] || '#000000FF'"
                    mode="hexa"
                    @input="onImportantColorChange(field.key, $event)"
                  />
                </v-card>
              </v-menu>
            </v-col>
          </v-row>

          <div class="mt-3 mb-2">
            <div class="caption grey--text text--lighten-1 mb-1">
              All CSS variables
            </div>
            <v-textarea
              v-model="advancedCss"
              outlined
              :rows="showFullStylesheet ? 12 : 3"
              hint="Edit remaining CSS variables directly"
              persistent-hint
              @input="onAdvancedCssChange"
              class="advanced-css-textarea"
            />
            <v-btn
              text
              x-small
              class="mt-1 grey--text"
              @click="showFullStylesheet = !showFullStylesheet"
            >
              <v-icon x-small left>
                {{ showFullStylesheet ? "mdi-chevron-up" : "mdi-chevron-down" }}
              </v-icon>
              {{ showFullStylesheet ? "Show less" : "Show all" }}
            </v-btn>
          </div>
        </v-card>
        <v-card outlined class="px-4 ma-2">
          <v-card-title class="pa-0 my-4">Project contact email</v-card-title>
          <p>Enter desired contact email for this project</p>
          <v-text-field
            v-model="projectSettings.contactEmail"
            label="Contact email"
            @input="updateChangeDetected(true)"
          ></v-text-field>
        </v-card>
      </v-col>

      <v-col>
        <v-card outlined class="px-4 ma-2">
          <v-card-title class="pa-0 my-4">Supported filters</v-card-title>
          <p>Check filters you want your project to support</p>

          <!-- Non-price filters: inline, 2-row collapse -->
          <div
            ref="filterList"
            class="inline-list"
            :class="{ 'inline-list--collapsed': !showAllFilters }"
          >
            <v-checkbox
              v-for="filter in nonPriceFilters"
              :key="filter.id"
              :class="['mr-4', 'flex-grow-0', filter.name]"
              v-model="projectSettings.selectedFilters"
              :label="filter.name"
              :value="filter.id"
              color="primary"
              @change="
                selectDefaultLang(),
                  updateChangeDetected(true, projectSettings.selectedFilters)
              "
            ></v-checkbox>
          </div>
          <v-btn
            v-if="filtersOverflow || showAllFilters"
            text
            x-small
            class="mb-3 grey--text"
            @click="showAllFilters = !showAllFilters"
          >
            <v-icon x-small left>
              {{ showAllFilters ? "mdi-chevron-up" : "mdi-chevron-down" }}
            </v-icon>
            {{ showAllFilters ? "Show less" : "Show all" }}
          </v-btn>

          <!-- Price filter: its own row with role sub-checkboxes -->
          <div v-if="priceFilter" class="price-filter-row mt-1">
            <v-checkbox
              v-model="projectSettings.selectedFilters"
              :label="priceFilter.name"
              :value="priceFilter.id"
              color="primary"
              class="mr-4 flex-grow-0"
              @change="
                selectDefaultLang(),
                  updateChangeDetected(true, projectSettings.selectedFilters)
              "
            ></v-checkbox>
            <span class="caption grey--text mr-2 price-roles-label">
              Visible to:
            </span>
            <v-checkbox
              v-for="role in roleList"
              :key="role.id"
              v-model="projectSettings.selectedRoles"
              :label="role.name"
              :value="role.id"
              color="primary"
              class="mr-3 flex-grow-0"
              :disabled="disablePrice"
              @change="selectDefaultLang(), updateChangeDetected(true)"
            ></v-checkbox>
          </div>
        </v-card>
        <v-card outlined class="px-4 ma-2">
          <div class="d-flex align-center justify-space-between my-4">
            <v-card-title class="pa-0">Project logo</v-card-title>
            <img
              v-if="projectSettings.logo"
              :src="projectSettings.logo"
              alt="Project logo"
              class="logo-preview"
            />
          </div>
          <p>Enter desired logo link for this project, or upload an image</p>
          <logo-upload-field
            v-model="projectSettings.logo"
            label="Logo link"
            upload-action="ProjectStore/uploadLogo"
            :owner-id="projectSettings.projectId"
            :disabled="!projectSettings.projectId"
            @input="updateChangeDetected(true)"
          ></logo-upload-field>

          <div class="d-flex align-center logo-scale-row">
            <div class="flex-grow-1">
              <template v-if="!projectSettings.logoScaleSeparate">
                <v-slider
                  v-model="logoScaleUniform"
                  label="Logo scale"
                  :min="LOGO_SCALE_MIN"
                  :max="LOGO_SCALE_MAX"
                  step="5"
                  thumb-label
                  hide-details
                  :disabled="!projectSettings.logo"
                >
                  <template v-slot:thumb-label="{ value }">
                    {{ value }}
                  </template>
                  <template v-slot:append>
                    <span class="caption logo-scale-value">
                      {{ projectSettings.logoScaleX }}%
                    </span>
                  </template>
                </v-slider>
              </template>
              <template v-else>
                <v-slider
                  v-model="projectSettings.logoScaleX"
                  label="Horizontal"
                  :min="LOGO_SCALE_MIN"
                  :max="LOGO_SCALE_MAX"
                  step="5"
                  thumb-label
                  hide-details
                  :disabled="!projectSettings.logo"
                  @input="updateChangeDetected(true)"
                >
                  <template v-slot:append>
                    <span class="caption logo-scale-value">
                      {{ projectSettings.logoScaleX }}%
                    </span>
                  </template>
                </v-slider>
                <v-slider
                  v-model="projectSettings.logoScaleY"
                  label="Vertical"
                  :min="LOGO_SCALE_MIN"
                  :max="LOGO_SCALE_MAX"
                  step="5"
                  thumb-label
                  hide-details
                  :disabled="!projectSettings.logo"
                  @input="updateChangeDetected(true)"
                >
                  <template v-slot:append>
                    <span class="caption logo-scale-value">
                      {{ projectSettings.logoScaleY }}%
                    </span>
                  </template>
                </v-slider>
              </template>
            </div>
            <v-checkbox
              v-model="logoScaleSeparate"
              label="Separate horizontal / vertical"
              color="primary"
              hide-details
              class="ml-4 mt-0 pt-0 flex-grow-0 logo-scale-checkbox"
              :disabled="!projectSettings.logo"
            ></v-checkbox>
          </div>
        </v-card>
        <v-card outlined class="px-4 ma-2">
          <v-card-title class="pa-0 my-4">Project website</v-card-title>
          <p>Enter desired website link for this project</p>
          <v-text-field
            v-model="projectSettings.website"
            label="Website link"
            @input="updateChangeDetected(true)"
          ></v-text-field>
        </v-card>
        <v-card outlined class="px-4 ma-2">
          <v-card-title class="pa-0 my-4">Project type</v-card-title>
          <p>
            Enable to sell this project as land parcels instead of apartments
          </p>
          <v-switch
            v-model="isParcelProject"
            label="Parcel project"
            @change="updateChangeDetected(true)"
          ></v-switch>
        </v-card>
        <v-card outlined class="px-4 ma-2">
          <v-card-title class="pa-0 my-4">Fall-through navigation</v-card-title>
          <p>
            Enable to send every visitor straight to a chosen masterplan (and
            optionally a specific building within it), instead of the normal
            Splash &rarr; Masterplan &rarr; Building flow. Once enabled,
            visitors cannot navigate to anything outside that part of the
            hierarchy.
          </p>
          <v-switch
            v-model="fallThroughEnabled"
            label="Fall through to masterplan/building"
            @change="updateChangeDetected(true)"
          ></v-switch>
          <template v-if="fallThroughEnabled">
            <v-select
              :items="masterplanOptions"
              item-text="name"
              item-value="id"
              label="Masterplan"
              outlined
              clearable
              v-model="projectSettings.fallThroughMasterplanId"
              @change="onFallThroughMasterplanChange"
              hide-details="auto"
              class="mb-3"
            >
              <template v-slot:item="{ item, on, attrs }">
                <v-list-item v-bind="attrs" v-on="on">
                  <v-list-item-title
                    :style="{ paddingLeft: item.depth * 20 + 'px' }"
                  >
                    <span v-if="item.depth > 0" class="grey--text mr-1"
                      >&#8627;</span
                    >{{ item.name }}
                  </v-list-item-title>
                </v-list-item>
              </template>
              <template v-slot:selection="{ item }">
                {{ item.name }}
              </template>
            </v-select>
            <v-select
              v-if="projectSettings.fallThroughMasterplanId && !isParcelProject"
              :items="buildingOptionsList"
              item-text="name"
              item-value="id"
              label="Building (optional — leave blank to fall through to the masterplan only)"
              outlined
              clearable
              v-model="projectSettings.fallThroughBuildingId"
              @change="updateChangeDetected(true)"
              hide-details="auto"
            ></v-select>
          </template>
        </v-card>
      </v-col>
    </v-row>

    <v-card-actions class="justify-end">
      <v-btn small :disabled="!changeDetected" @click="cancel()"
        >Discard Changes</v-btn
      >
      <v-btn
        small
        :disabled="!changeDetected"
        color="primary darken-1"
        @click="saveSettingsDialog()"
        >Save</v-btn
      >
    </v-card-actions>
  </v-container>
</template>

<script>
import Enums from "../helpers/Enums";
import { EventBus } from "../helpers/Events";
import MasterplanService from "../services/MasterplanService";
import MasterplanHelper from "../helpers/MasterplanHelper";
import LogoUploadField from "../components/LogoUploadField.vue";

const IMPORTANT_VAR_DEFS = [
  { key: "--v-primary-base", label: "Primary" },
  { key: "--v-primary-opaque", label: "Primary (opacity)" },
  { key: "--v-button-background", label: "Button background" },
  { key: "--v-button-highlighted", label: "Button highlighted" },
  { key: "--v-button-text", label: "Button text" },
  { key: "--v-secondary-base", label: "Secondary" },
  { key: "--v-accent-base", label: "Accent" },
  { key: "--v-accent-opaque", label: "Accent (opacity)" },
  { key: "--v-offBlack", label: "Off black" },
  { key: "--v-offWhite", label: "Off white" },
  { key: "--v-offWhiteOpaque", label: "Off white (opacity)" },
  { key: "--v-highlightedPrimary-base", label: "Highlighted primary" },
  { key: "--v-viewswitch-background", label: "View switch background" },
  { key: "--v-viewswitch-arrow", label: "View switch arrow" },
];

const LOGO_SCALE_MIN = 25;
const LOGO_SCALE_MAX = 300;
const LOGO_SCALE_DEFAULT = 100;

export default {
  components: {
    LogoUploadField,
  },
  props: {
    currentTab: {
      required: false,
      type: String,
    },
  },
  data: function() {
    this.langList = Enums.lang;
    this.filterList = Enums.filters;
    this.roleList = Enums.roles;
    this.defaultLangOptions = [];
    this.LOGO_SCALE_MIN = LOGO_SCALE_MIN;
    this.LOGO_SCALE_MAX = LOGO_SCALE_MAX;
    return {
      projectSettings: {
        projectId: this.$route.params.projectId,
        selectedLangs: [],
        selectedFilters: [],
        selectedRoles: [],
        defaultLangId: 1,
        stylesheet: "",
        contactEmail: "",
        logo: "",
        website: "",
        isParcelProject: false,
        fallThroughEnabled: false,
        fallThroughMasterplanId: null,
        fallThroughBuildingId: null,
        logoScaleX: LOGO_SCALE_DEFAULT,
        logoScaleY: LOGO_SCALE_DEFAULT,
        logoScaleSeparate: false,
      },
      buildingOptionsList: [],
      changeDetected: false,
      disablePrice: false,
      parsedVars: {},
      advancedCss: "",
      importantVarFields: IMPORTANT_VAR_DEFS.map((d) => ({
        ...d,
        pickerOpen: false,
      })),
      showAllLangs: false,
      showAllFilters: false,
      showFullStylesheet: false,
      langsOverflow: false,
      filtersOverflow: false,
    };
  },
  computed: {
    langOptions() {
      let arr = [];
      Object.entries(this.langList).forEach(([, value]) => {
        if (this.projectSettings.selectedLangs.includes(value.id)) {
          arr.push(value);
        }
      });
      return arr;
    },
    nonPriceFilters() {
      return Object.values(this.filterList).filter((f) => f.name !== "Price");
    },
    priceFilter() {
      return Object.values(this.filterList).find((f) => f.name === "Price");
    },
    projectConfig() {
      return this.$store.state.ProjectStore.projectConfig;
    },
    isParcelProject: {
      get() {
        return !!this.projectSettings.isParcelProject;
      },
      set(val) {
        this.projectSettings.isParcelProject = val;
      },
    },
    fallThroughEnabled: {
      get() {
        return !!this.projectSettings.fallThroughEnabled;
      },
      set(val) {
        this.projectSettings.fallThroughEnabled = val;
        if (!val) {
          this.projectSettings.fallThroughMasterplanId = null;
          this.projectSettings.fallThroughBuildingId = null;
        }
      },
    },
    viewSwitchBackground() {
      return (
        this.parsedVars["--v-viewswitch-background"] ||
        this.parsedVars["--v-accent-opaque"] ||
        this.parsedVars["--v-button-background"] ||
        "#a79160"
      );
    },
    viewSwitchArrow() {
      return this.parsedVars["--v-viewswitch-arrow"] || "#fefbf5";
    },
    logoScaleStyle() {
      // Same transform the Property Navigator applies: scale from the
      // logo's own center, so the mockup matches what visitors see.
      const { logoScaleX: x, logoScaleY: y } = this.projectSettings;
      return {
        transform: `scale(${x / 100}, ${y / 100})`,
        transformOrigin: "center",
      };
    },
    // One slider drives both axes while "separate" is off.
    logoScaleUniform: {
      get() {
        return this.projectSettings.logoScaleX;
      },
      set(val) {
        this.projectSettings.logoScaleX = val;
        this.projectSettings.logoScaleY = val;
        this.updateChangeDetected(true);
      },
    },
    logoScaleSeparate: {
      get() {
        return !!this.projectSettings.logoScaleSeparate;
      },
      set(val) {
        this.projectSettings.logoScaleSeparate = val;
        // Collapsing back to one slider: keep the horizontal value as the
        // uniform one so the two axes never silently disagree.
        if (!val) this.projectSettings.logoScaleY = this.projectSettings.logoScaleX;
        this.updateChangeDetected(true);
      },
    },
    masterplanOptions() {
      // projectMasterplans is the full flat list for the project (root +
      // nested/child masterplans), so a fall-through target can be set
      // directly on a deeply-nested masterplan, not just a top-level one.
      // Built as a depth-first tree (not just filtered) so the picker's
      // item slot can indent each row by its real depth and the
      // parent/child relationship is visible, not just a flat id-ordered
      // list with a "(nested)" label.
      const all = this.$store.state.MasterplanStore.projectMasterplans || [];
      return MasterplanHelper.buildMasterplanTreeOptions(all);
    },
  },
  watch: {
    currentTab: function(_, oldVal) {
      if (oldVal === "projectSettings") {
        this.cancel();
      }
    },
  },
  mounted() {
    this.$store.dispatch("MasterplanStore/fetchMasterplans");
    this.getProjectConfig();
    EventBus.$on("saveSettings", this.SaveSettings);
    this.$nextTick(this.checkOverflow);
  },
  destroyed() {
    EventBus.$off("saveSettings");
  },
  methods: {
    checkOverflow() {
      const langEl = this.$refs.langList;
      if (langEl)
        this.langsOverflow = langEl.scrollHeight > langEl.clientHeight;
      const filterEl = this.$refs.filterList;
      if (filterEl)
        this.filtersOverflow = filterEl.scrollHeight > filterEl.clientHeight;
    },
    updateChangeDetected(val, selectedFilters) {
      if (selectedFilters) {
        this.priceCheckboxState(selectedFilters);
      }
      this.changeDetected = val;
      this.$emit("changeDetectedSettings", val);
    },
    async saveSettingsDialog() {
      let subtitle = "Are you sure?";
      if (
        this.projectSettings.fallThroughEnabled &&
        this.projectSettings.fallThroughMasterplanId
      ) {
        try {
          const summary = await MasterplanService.getPublishSummary(
            this.projectSettings.fallThroughMasterplanId,
            this.projectSettings.fallThroughBuildingId,
          ).then((res) => res.data);
          const otherPublished =
            (summary.siblingMasterplansPublished || 0) +
            (summary.siblingBuildingsPublished || 0);
          if (otherPublished > 0) {
            subtitle = `There are ${otherPublished} other published masterplans/buildings that will no longer be reachable through normal navigation once this is saved. Are you sure you want to continue?`;
          }
        } catch (e) {
          // Non-fatal — fall back to the generic confirmation if the check fails.
        }
      }
      this.$store.dispatch("DialogStore/open", {
        title: "Save changes",
        header: false,
        subtitle,
        okText: "Ok",
        ok: () => {
          this.SaveSettings();
        },
        cancelText: "Cancel",
      });
    },
    async onFallThroughMasterplanChange(masterplanId) {
      this.projectSettings.fallThroughBuildingId = null;
      this.buildingOptionsList = [];
      if (masterplanId) {
        await this.loadBuildingOptions(masterplanId);
      }
      this.updateChangeDetected(true);
    },
    async loadBuildingOptions(masterplanId) {
      const res = await MasterplanService.getBuildingsForMasterplan(
        masterplanId,
      );
      this.buildingOptionsList = res.data || [];
    },
    async SaveSettings() {
      await this.$store.dispatch(
        "ProjectStore/setSelectedProjectSettings",
        this.projectSettings,
      );
      const existingProperties =
        this.$store.state.ProjectStore.selectedProject.properties || {};
      await this.$store.dispatch("ProjectStore/updateProject", {
        id: this.projectSettings.projectId,
        stylesheet: this.projectSettings.stylesheet,
        contactEmail: this.projectSettings.contactEmail,
        logo: this.projectSettings.logo,
        website: this.projectSettings.website,
        isParcelProject: this.projectSettings.isParcelProject,
        properties: {
          ...existingProperties,
          logoScale: {
            x: this.projectSettings.logoScaleX,
            y: this.projectSettings.logoScaleY,
            separate: this.projectSettings.logoScaleSeparate,
          },
          fallThrough: {
            enabled: this.projectSettings.fallThroughEnabled,
            masterplanId: this.projectSettings.fallThroughEnabled
              ? this.projectSettings.fallThroughMasterplanId
              : null,
            buildingId: this.projectSettings.fallThroughEnabled
              ? this.projectSettings.fallThroughBuildingId
              : null,
          },
        },
      });
      this.cancel();
      this.dialog = false;
    },
    async cancel() {
      await this.$store.dispatch(
        "ProjectStore/getSelectedProjectSettings",
        this.$route.params.projectId,
      );
      this.getProjectConfig();
      this.updateChangeDetected(false);
    },
    priceCheckboxState(selectedFilters) {
      var filters = [];
      for (const [propertyKey, propertyValue] of Object.entries(
        Enums.filters,
      )) {
        if (propertyValue.name !== "Price") continue;
        filters.push({ propertyKey, propertyValue });
      }
      if (selectedFilters.includes(filters[0].propertyValue.id)) {
        this.disablePrice = false;
      } else {
        this.disablePrice = true;
      }
    },
    getProjectConfig() {
      this.projectSettings.stylesheet = this.$store.state.ProjectStore.selectedProject.stylesheet;
      this.projectSettings.contactEmail = this.$store.state.ProjectStore.selectedProject.contactEmail;
      this.projectSettings.logo = this.$store.state.ProjectStore.selectedProject.logo;
      this.projectSettings.website = this.$store.state.ProjectStore.selectedProject.website;
      this.projectSettings.isParcelProject = this.$store.state.ProjectStore.selectedProject.isParcelProject || false;
      // Fall-through settings live inside the project's properties JSON blob
      // rather than as dedicated project columns.
      const fallThrough = this.$store.state.ProjectStore.selectedProject.properties &&
        this.$store.state.ProjectStore.selectedProject.properties.fallThrough || {};
      this.projectSettings.fallThroughEnabled = fallThrough.enabled || false;
      this.projectSettings.fallThroughMasterplanId = fallThrough.masterplanId || null;
      this.projectSettings.fallThroughBuildingId = fallThrough.buildingId || null;
      const logoScale = this.$store.state.ProjectStore.selectedProject.properties &&
        this.$store.state.ProjectStore.selectedProject.properties.logoScale || {};
      this.projectSettings.logoScaleX = logoScale.x || LOGO_SCALE_DEFAULT;
      this.projectSettings.logoScaleY = logoScale.y || LOGO_SCALE_DEFAULT;
      this.projectSettings.logoScaleSeparate = !!logoScale.separate;
      if (this.projectSettings.fallThroughMasterplanId) {
        this.loadBuildingOptions(this.projectSettings.fallThroughMasterplanId);
      }
      this.projectSettings.selectedLangs = this.projectConfig[
        this.projectConfig.length - 1
      ].mediaData.selectedLangs;
      const filters = this.projectConfig[this.projectConfig.length - 1]
        .mediaData.selectedFilters;
      if (!filters) {
        this.projectSettings.selectedFilters = Object.keys(Enums.filters).map(
          (f) => Number(f),
        );
        this.SaveSettings();
      }
      this.projectSettings.selectedFilters = filters;
      const roles = this.projectConfig[this.projectConfig.length - 1].mediaData
        .selectedRoles;
      if (!roles) {
        this.projectSettings.selectedRoles = Object.keys(Enums.roles).map((f) =>
          Number(f),
        );
        this.SaveSettings();
      }
      this.projectSettings.selectedRoles = roles;
      this.projectSettings.defaultLangId = this.projectConfig[
        this.projectConfig.length - 1
      ].mediaData.defaultLangId;
      this.priceCheckboxState(this.projectSettings.selectedFilters);
      this.selectDefaultLang();
      this.loadStylesheetVars();
    },
    selectDefaultLang() {
      if (
        !this.projectSettings.selectedLangs.includes(
          this.projectSettings.defaultLangId,
        )
      ) {
        this.projectSettings.defaultLangId = this.projectSettings.selectedLangs[0];
      }
    },
    parseStylesheetVars(css) {
      const vars = {};
      if (!css) return vars;
      const re = /(--[\w-]+)\s*:\s*([^;]+);/g;
      let m;
      while ((m = re.exec(css)) !== null) {
        vars[m[1].trim()] = m[2].trim();
      }
      return vars;
    },
    loadStylesheetVars() {
      const allVars = this.parseStylesheetVars(this.projectSettings.stylesheet);
      const importantKeys = new Set(IMPORTANT_VAR_DEFS.map((d) => d.key));
      this.parsedVars = allVars;
      const advLines = Object.entries(allVars)
        .filter(([k]) => !importantKeys.has(k))
        .map(([k, v]) => `  ${k}: ${v};`);
      this.advancedCss = advLines.join("\n");
    },
    syncStylesheet() {
      const importantKeys = new Set(IMPORTANT_VAR_DEFS.map((d) => d.key));
      const lines = [];
      for (const { key } of IMPORTANT_VAR_DEFS) {
        if (this.parsedVars[key]) {
          lines.push(`  ${key}: ${this.parsedVars[key]};`);
        }
      }
      Object.entries(this.parsedVars)
        .filter(([k]) => !importantKeys.has(k))
        .forEach(([k, v]) => lines.push(`  ${k}: ${v};`));
      this.projectSettings.stylesheet = `:root{\n${lines.join("\n")}\n}`;
    },
    onImportantColorChange(key, value) {
      if (!value || typeof value !== "string") return;
      if (!/^#[0-9a-fA-F]{6,8}$/.test(value)) return;
      this.$set(this.parsedVars, key, value.toLowerCase());
      this.syncStylesheet();
      this.updateChangeDetected(true);
    },
    onAdvancedCssChange() {
      const importantKeys = new Set(IMPORTANT_VAR_DEFS.map((d) => d.key));
      const advVars = this.parseStylesheetVars(
        `:root{\n${this.advancedCss}\n}`,
      );
      Object.keys(this.parsedVars).forEach((k) => {
        if (!importantKeys.has(k)) this.$delete(this.parsedVars, k);
      });
      Object.entries(advVars).forEach(([k, v]) => {
        this.$set(this.parsedVars, k, v);
      });
      this.syncStylesheet();
      this.updateChangeDetected(true);
    },
  },
};
</script>

<style scoped>
.inline-list {
  display: flex;
  flex-wrap: wrap;
}
.inline-list--collapsed {
  max-height: 120px;
  overflow: hidden;
}
.price-filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding-top: 4px;
}
.price-roles-label {
  align-self: center;
  white-space: nowrap;
}
.stylesheet-preview {
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  overflow: hidden;
  padding: 10px;
}
/* Wrapper gives the pill bar its floating margin (mirrors ma-6 in MainAppBar) */
.preview-appbar-wrapper {
  padding: 0 4px;
  margin-bottom: 10px;
}
/* Pill bar: rounded, box-shadow, backdrop-blur — mirrors MainAppBar exactly */
.preview-appbar {
  display: flex;
  align-items: center;
  padding: 6px 10px;
  min-height: 40px;
  border-radius: 50px;
  box-shadow: 0px 4px 6px 2px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(4px);
}
.preview-appbar-left {
  display: flex;
  align-items: center;
  flex: 1;
}
.preview-appbar-center {
  display: flex;
  justify-content: center;
  flex: 2;
}
.preview-appbar-right {
  display: flex;
  justify-content: flex-end;
  flex: 1;
}
.preview-logo {
  max-height: 18px;
  max-width: 60px;
  object-fit: contain;
}
.preview-appbar-title {
  font-size: 11px;
  font-weight: bold;
  letter-spacing: 2.75px;
}
.preview-viewswitch {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.preview-viewswitch-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  backdrop-filter: blur(8px);
}
.preview-body {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 4px;
  min-height: 72px;
}
.preview-unit-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 6px;
  flex: 1;
  gap: 8px;
}
.preview-unit-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
}
.preview-accent-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
/* Mirrors ComponentMainButton: pill, depressed (no border/shadow), letter-spacing 2.7px */
.preview-action-btn {
  padding: 6px 16px;
  border-radius: 50px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  letter-spacing: 2.7px;
  cursor: default;
}
.preview-badge {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}
.color-swatch-btn {
  display: flex;
  align-items: center;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 4px;
  padding: 5px 8px;
  transition: border-color 0.2s;
}
.color-swatch-btn:hover {
  border-color: rgba(255, 255, 255, 0.35);
}
.color-swatch-preview {
  width: 24px;
  height: 24px;
  border-radius: 3px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  flex-shrink: 0;
}
.swatch-hex {
  font-family: monospace;
  font-size: 11px;
}
.advanced-css-textarea >>> textarea {
  font-family: monospace;
  font-size: 12px;
}
.logo-scale-row {
  margin-top: 12px;
}
.logo-scale-value {
  min-width: 36px;
  text-align: right;
}
.logo-scale-checkbox {
  max-width: 200px;
}
.logo-preview {
  max-height: 72px;
  max-width: 240px;
  object-fit: contain;
  border-radius: 4px;
  margin-right: 12px;
}
</style>
